import React, { useEffect, useRef } from "react";
import moment from "moment";
import Uri from "jsuri";
import numeral from "../../../../node_modules/numeraljs/numeral.js";
import isEqual from "lodash/fp/isEqual";
import debounce from "lodash/fp/debounce";

// Query Elements
export const QueryElementsFromURL = (params) =>
  params.filter(
    (item) => item.type === "metrics" || item.kind === "broken_out_by"
  );

// Query Filters
export const QueryFiltersFromURL = (params) =>
  params.filter((item) => item.kind === "filter_by");

export const useDeepCompare = (fn, deps) => {
  const isFirst = useRef(true);
  const prevDeps = useRef(deps);

  useEffect(() => {
    const isSame = prevDeps.current.every((obj, index) =>
      isEqual(obj, deps[index])
    );

    if (isFirst.current || !isSame) {
      fn();
    }

    isFirst.current = false;
    prevDeps.current = deps;
  }, [deps, fn]);
};

// Valid query requires at least one metric
export const hasMetrics = (state) =>
  state &&
  state.query &&
  state.query.hasOwnProperty("metrics") &&
  state.query.metrics.length > 0;

// Check for presence of query params
export const hasQuery = (state) =>
  state && state.query && Object.keys(state.query).length !== 0;

// Extract values from hash url or provide a default
export const getQueryParam = (query, param) =>
  query ? query.find((o) => o[param])[param] : "";

export const formatPercentage = (value) => {
  let percent_format = "";

  if (Math.sign(value) === 1) {
    if (value <= 0.01 && value > 0) {
      percent_format = "0.00%";
    } else if (value > 0.01 && value < 0.1) {
      percent_format = "0.0%";
    } else if (value >= 0.1) {
      percent_format = "0%";
    } else {
    }
  } else {
    if (value >= -0.01 && value < 0) {
      percent_format = "0.00%";
    } else if (value < -0.01 && value > -0.1) {
      percent_format = "0.0%";
    } else if (value <= -0.1) {
      percent_format = "0%";
    } else {
    }
  }

  return numeral(value).format(percent_format);
};

/**
 * Returns formatted metric according to its type.
 * The type of a metric is derived from metrics meta information which are loaded from META graphql request.
 * Once the meta information are downloaded they are added to the global context.
 *
 * @param {Number} id - metric's id
 * @param {Number} value - metric's numeric value
 * @param {Array} state - global state with metrics
 */
export const formatQueryMetric = (id, value, state, type) => {
  const metricValueType = state.find(
    (metric) => parseInt(metric.id) === parseInt(id)
  );
  // there are some cases where the `state` is still empty because it wasn't
  // processed yet.. so, return its value till it get valid
  if (!metricValueType) return value;

  if (type === "segments" || type === "table" || type === "pivot") {
    switch (metricValueType.valueType) {
      // for time, it should should the real values rather than getting this shrinked (for more details: RDMP2021-1515)
      case "time":
        return `${numeral(value).format("0,0")} min`;

      case "date":
        return moment(value, ["MM/DD", "YYYY-MM-DD"]).format("YYYY-MM-DD");

      case "percentage":
        return formatPercentage(value);

      case "currency":
        return numeral(value).format("$0,0.00");

      default:
        return numeral(value).format("0,0");
    }
  } else {
    switch (metricValueType.valueType) {
      case "time":
        return `${numeral(value).format("0,0")} min`;

      case "date":
        return moment(value, ["MM/DD", "YYYY-MM-DD"]).format("YYYY-MM-DD");

      case "currency":
        return value >= 1000
          ? numeral(value).format("$0.0a")
          : numeral(value).format("$0.00");

      case "percentage":
        return formatPercentage(value);

      default:
        return value >= 999
          ? numeral(value).format("0.0a")
          : numeral(value).format("0a");
    }
  }
};

/**
 * Query Segment Tracking Helper
 *
 * @param {string} type - type of query visualization
 * @param {Object} state - state
 * @param {Object} data - query data results
 * @param {Array} pastedQueryFilters - query filters (dimensions and dimension items)
 * @param {String} friendlyDateRangeName - friendly date range name
 * @param {String} exportOption - type of export
 * @param {String} currentTenant - current tenant name
 */

const querySegmentTrackingDebounce = (
  type,
  state,
  data,
  pastedQueryFilters,
  friendlyDateRangeName,
  exportOption,
  currentTenant,
  currentProject
) => {
  data = Array.isArray(data) ? data : [];
  let query_metrics_ids = [];
  let query_metrics_names = [];
  let query_metrics_friendly_names = [];

  let query_dimensions_friendly_names = [];
  let query_dimensions_ids = [];
  let query_dimensions_names = [];

  let query_dimensions_items_names = [];
  let query_dimensions_items_friendly_names = [];

  data.forEach((el) => {
    el.metrics.forEach((ind) => {
      query_metrics_ids.push(parseInt(ind.id));
      query_metrics_names.push(ind.name);
      query_metrics_friendly_names.push(ind.friendlyName);

      query_metrics_ids = [...new Set(query_metrics_ids)];
      query_metrics_names = [...new Set(query_metrics_names)];
      query_metrics_friendly_names = [...new Set(query_metrics_friendly_names)];
    });
    if (el.dimensions) {
      el.dimensions.forEach((ind) => {
        query_dimensions_ids.push(ind.id);
        query_dimensions_names.push(ind.name);
        query_dimensions_friendly_names.push(ind.friendlyName);

        query_dimensions_ids = [...new Set(query_dimensions_ids)];
        query_dimensions_names = [...new Set(query_dimensions_names)];
        query_dimensions_friendly_names = [
          ...new Set(query_dimensions_friendly_names),
        ];
      });
    }
  });

  if (pastedQueryFilters && Array.isArray(pastedQueryFilters)) {
    pastedQueryFilters.forEach((el) => {
      if (el.type === "dimensions_items" && el.value) {
        el.value.forEach((ind) => {
          query_dimensions_items_names.push(ind.name);
          query_dimensions_items_friendly_names.push(ind.friendlyName);

          query_dimensions_items_names = [
            ...new Set(query_dimensions_items_names),
          ];
          query_dimensions_items_friendly_names = [
            ...new Set(query_dimensions_items_friendly_names),
          ];
        });
      } else if (el.type === "dimensions") {
        query_dimensions_names.push(el.name);
        query_dimensions_friendly_names.push(el.friendlyName);

        query_dimensions_names = [...new Set(query_dimensions_names)];
        query_dimensions_friendly_names = [
          ...new Set(query_dimensions_friendly_names),
        ];
      }
    });
  }

  switch (type) {
    case "query-data":
      window.analytics.track("Query Builder Query", {
        query_visualization: state.query.chartType,
        query_granularity: state.query.granularity,
        query_start_date: moment(state.query.startDate).format(
          "dddd, MMMM Do, YYYY h:mm:ss A"
        ),
        query_end_date: moment(state.query.endDate).format(
          "dddd, MMMM Do, YYYY h:mm:ss A"
        ),

        query_date_range_name: friendlyDateRangeName,

        query_metrics_ids: query_metrics_ids.join(", "),
        query_metrics_names: query_metrics_names.join(", "),
        query_metrics_friendly_names: query_metrics_friendly_names.join(", "),

        query_dimensions_ids: query_dimensions_ids.join(", "),
        query_dimensions_names: query_dimensions_names.join(", "),
        query_dimensions_friendly_names:
          query_dimensions_friendly_names.join(", "),

        query_dimensions_items_names: query_dimensions_items_names.join(", "),
        query_dimensions_items_friendly_names:
          query_dimensions_items_friendly_names.join(", "),
        board: currentProject.board,
        project: currentProject.project,
      });
      break;
    case "query-no-data":
      window.analytics.track("Query Builder: No Data", {
        query: window.location.href,
        tenant: currentTenant,
        board: currentTenant.board,
        project: currentTenant.project,
      });
      break;
    case "error":
      window.analytics.track("Query Builder: Error", {
        query: window.location.href,
        tenant: currentTenant,
        project: currentProject.project,
        errorMessage: data.errorMessage,
        errorCode: data.errorCode,
        internalErrorCode: data.internalErrorCode,
        timestamp: moment(data.timestamp).format(
          "dddd, MMMM Do, YYYY h:mm:ss A"
        ),
      });

      break;
    case "csv-export":
      window.analytics.track("Query Builder Export", {
        query_metrics_ids: query_metrics_ids.join(", "),
        query_metrics_names: query_metrics_names.join(", "),
        query_metrics_friendly_names: query_metrics_friendly_names.join(", "),

        query_dimensions_ids: query_dimensions_ids.join(", "),
        query_dimensions_names: query_dimensions_names.join(", "),
        query_dimensions_friendly_names:
          query_dimensions_friendly_names.join(", "),

        query_dimensions_items_names: query_dimensions_items_names.join(", "),
        query_dimensions_items_friendly_names:
          query_dimensions_items_friendly_names.join(", "),
        query_export_type: exportOption ? exportOption.join(", ") : "CSV",

        query_date_range_name: friendlyDateRangeName,
        board: currentProject.board,
        project: currentProject.project,
        tenant: currentTenant,
      });
      break;
    default:
      break;
  }
};

export const querySegmentTracking = debounce(
  5000,
  querySegmentTrackingDebounce
);

// Toggle for loading hidden metrics
export const showHiddenMetrics =
  Boolean(
    new Uri(window.location.search).getQueryParamValue("hiddenmetrics")
  ) || false;

// Toggle for loading hidden dimensions
export const showHiddenDimensions =
  Boolean(
    new Uri(window.location.search).getQueryParamValue("hiddendimensions")
  ) || false;

// Toggle environments
export const environment = () => {
  const host = window.location.host;
  const subdomain = host.split(".")[0];
  const urlParam =
    new Uri(window.location.search).getQueryParamValue("env") || false;
  let env = "production";

  if (urlParam) {
    env = urlParam;
  } else if (subdomain !== "dashboard" && subdomain !== "staging") {
    env = "development";
  }

  return env;
};

/**
 * Returns metrics or dimensions grouped by type or filtered by props
 * @param {*} options
 */
export const groupMetricByCategory = (meta) => {
  const groupedData = [];
  const categories = Array.from(
    new Set(meta.map((metric) => metric.category.friendlyName))
  ).sort();
  for (let category of categories) {
    groupedData.push({
      id: category,
      friendlyName: category,
      node: true,
    });
    groupedData.push(
      ...meta
        .filter((m) => m.category.friendlyName === category)
        .sort((a, b) => (a.friendlyName > b.friendlyName ? 1 : -1))
    );
  }
  return groupedData;
};

/**
 * Creates a different shape for the predictions bars
 * @param {*} x
 * @param {*} y
 * @param {*} width
 * @param {*} height
 * @param {*} dataKey
 * @returns
 */
export const getPredictionBarShape = (
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  dataKey
) => {
  return ({ fill }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <defs>
        <pattern
          id={dataKey}
          patternUnits="userSpaceOnUse"
          width="6"
          height="4"
          patternTransform="rotate(146)"
        >
          <line x1="0" y="0" x2="0" y2="6" stroke={fill} strokeWidth="11" />
        </pattern>
      </defs>
      <rect
        width={width || 0}
        height={height || 0}
        x={x || 0}
        y={y || 0}
        fill={"url(#" + dataKey + ")"}
        stroke={"url(#" + dataKey + ")"}
        opacity="0.4"
      />
    </svg>
  );
};

export const formatDate = (granularity, showTime) => {
  let format = "MM/YY";
  switch (granularity) {
    case "year":
      format = "YYYY/MM/DD";
      break;

    case "month":
      format = "MM/YY";
      break;

    case "week":
      format = "MM/DD";
      break;

    case "day":
      format = "MM/DD";
      break;

    case "hour":
      if (showTime) {
        format = "YYYY/MM/DD - h:mm A";
      } else {
        format = "HH";
      }
      break;

    case "quarter":
      format = "MM/DD";
      break;

    default:
      format = "MM/YY";
      break;
  }
  return format;
};
